/*globals moment, login*/
import React, { useContext, useEffect } from "react";
import { isValidIPValue, parseClusterResponse, validateIPv4 } from "common/api";
import Modal from "common/Modal";
import { useState } from "react";
import BqnContext from "common/BqnContext";
import { parseManagementInterface } from "views/Config/Interfaces/Management/api";
import NumberInputWithSpinner from "common/NumberInputWithSpinnerState";

const doesNothing = () => {};

export const AddBqnModal = ({ onOk = doesNothing, onClose = doesNothing }) => {
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);
  const [bqn, setBqn] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [managementIpAddress, setManagementIpAddress] = useState("");
  const [publicIpAddress, setPublicIpAddress] = useState("");
  const [sshPort, setSshPort] = useState(22);
  const [publicSshPort, setPublicSshPort] = useState(22);

  const {
    bqnId,
    bqns,
    loadClusterBqn,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  // Only the BQN of a standalone answers here, and it answers for itself, so
  // the command carries no "bqn-id ID" option: nothing is asked while the
  // mode is not known yet, when the option would be the wrong one either way.
  useEffect(() => {
    if (mode !== "standalone") {
      return;
    }

    ifCl
      .run("show interface management detail")
      .then((response) => {
        const parsedAddress = parseClusterResponse(
          response,
          parseManagementInterface,
          bqnId
        )[bqnId]?.address;
        setManagementIpAddress(parsedAddress);
        setPublicIpAddress(parsedAddress);
      });
  }, [mode, bqnId]);

  const doClose = () => onClose();
  if (open === false) {
    return null;
  }

  const getConfigureCmdCluster = () =>
    `configure\ncluster\nbqn ${bqn} ${ipAddress}${
      sshPort === 22 ? "" : ` ${sshPort}`
    }\ncommit\n`;

  function getConfigureCmdStandalone() {
    if (!managementIpAddress) {
      throw new Error("No management IP address found");
    }
    return `configure\ncluster\nbqn ${bqn} ${ipAddress}${
      sshPort === 22 ? "" : ` ${sshPort}`
    }\nbqn ${bqnId} ${publicIpAddress}${
      publicSshPort === 22 ? "" : ` ${publicSshPort}`
    }\ncommit\n`;
  }

  async function doApply() {
    if (!bqn || isNaN(bqn) || Number(bqn) < 0 || Number(bqn) > 254) {
      return setError("BqnId must be a number between 0 and 255");
    }

    if (!isValidIPValue(ipAddress)) {
      return setError("Invalid IP address");
    }

    if (mode === "standalone" && !validateIPv4(publicIpAddress)) {
      return setError("Invalid IP address entered for this BQN");
    }

    if (
      mode === "cluster" &&
      bqns.find(
        (item) =>
          parseFloat(bqn) === parseFloat(item.bqnId) ||
          ipAddress === item.ipAddress
      )
    ) {
      return setError("BQN already exists in the cluster");
    }

    if (mode === "standalone") {
      ifCl
        .run(`system bqn id ${bqnId} force`)
        .then(() => {
          configureCluster();
        })
        .catch((response) => {
          return ifCl
            .run("configure\nclear config changes\n", true)
            .then(() => {
              setError(response);
            });
        });
    } else {
      configureCluster();
    }
  }

  const handleSuccess = () => {
    onOk();
    doClose();
    if (mode === "standalone") {
      login.doLogout();
    } else {
      loadClusterBqn().catch(console.error);
    }
  };

  const sshSync = () => ifCl.run("system users ssh sync");

  function configureCluster() {
    const cmd =
      mode === "standalone"
        ? getConfigureCmdStandalone()
        : getConfigureCmdCluster();
    ifCl
      .run(cmd, true)
      .then(() => {
        sshSync()
          .then(() => {
            handleSuccess();
          })
          .catch((error) => {
            ifCl.run("configure\nclear config changes\n", true).then(() => {
              setError(error);
            });
          });
      })
      .catch((response) => {
        if (response.startsWith('%ERR-EINVAL: Invalid "bta" license')) {
          handleSuccess();
        } else {
          ifCl.run("configure\nclear config changes\n", true).then(() => {
            setError(response);
          });
        }
      });
  }

  return (
    <Modal
      title={
        mode === "standalone"
          ? "Add BQN to a cluster..."
          : "Add BQN to this cluster..."
      }
      superIcon="device_hub"
      large={true}
      className="title-no-transform"
      content={() => {
        return (
          <div className="row clearfix margin-b-40">
            {mode === "standalone" ? (
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <label>
                  Add the BQN ID, IP address and ssh port to contact the primary
                  node of the cluster you want to join. Alternatively, add the
                  BQN ID, IP address and ssh port of another stand-alone BQN to
                  create a new cluster with it.
                </label>
              </div>
            ) : null}
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
              <div className="form-group margin-b-0">
                <label>BQN ID: </label>
                <div className="input-group margin-b-0">
                  <span className="input-group-addon">
                    <i className="material-icons">computer</i>
                  </span>
                  <div className="form-line">
                    <input
                      type="text"
                      className="form-control align-left"
                      required
                      onChange={(event) => {
                        setError(null);
                        setBqn(event.target.value);
                      }}
                      value={bqn}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
              <div className="form-group margin-b-0">
                <label>IP Address:</label>
                <div className="input-group margin-b-0">
                  <span className="input-group-addon">
                    <i className="material-icons">cloud_queue</i>
                  </span>
                  <div className="form-line">
                    <input
                      type="text"
                      className="form-control align-left"
                      required
                      onChange={(event) => {
                        setError(null);
                        setIpAddress(event.target.value);
                      }}
                      value={ipAddress}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
              <div className="form-group margin-b-0">
                <label>SSH Port:</label>
                <div className="input-group margin-b-0">
                  <div className="padding-t-10">
                    <NumberInputWithSpinner
                      name="ssh port"
                      label={"noLabel"}
                      min={1}
                      hideReset
                      step={1}
                      initialValue={sshPort}
                      onChange={(value) => setSshPort(value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            {mode === "standalone" && (
              <>
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                  <label>
                    {`Enter the IP Address and ssh port for this BQN, which other BQNs in the cluster have to use for inter-cluster communication. It may be the local management IP address (${managementIpAddress}), or a public IP address that will be mapped to that management IP address for TCP ports 63500-63755:`}
                  </label>
                </div>
                <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
                  <div className="form-group margin-b-0">
                    <label>IP Address for this BQN in cluster:</label>
                    <div className="input-group margin-b-0">
                      <span className="input-group-addon">
                        <i className="material-icons">cloud_queue</i>
                      </span>
                      <div className="form-line">
                        <input
                          type="text"
                          className="form-control align-left"
                          required
                          onChange={(event) => {
                            setError(null);
                            setPublicIpAddress(event.target.value);
                          }}
                          value={publicIpAddress}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
                  <div className="form-group margin-b-0">
                    <label>SSH Port:</label>
                    <div className="input-group margin-b-0">
                      <div className="padding-t-10">
                        <NumberInputWithSpinner
                          name="ssh port"
                          label={"noLabel"}
                          min={1}
                          hideReset
                          step={1}
                          initialValue={publicSshPort}
                          onChange={(value) => setPublicSshPort(value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }}
      applyLabel="OK"
      onApply={doApply}
      closeLabel="CANCEL"
      onClose={onClose}
      footerMessage={
        (error !== null && (
          <span className="modal-err-msg color-red align-left">{error}</span>
        )) ||
        null
      }
    />
  );
};
